import queryString from "query-string";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getCurrentUser } from "@/auth/firebase";
import { Button } from "@/design-system/components/Button/Button";
import { Input } from "@/design-system/components/Input/Input";
import { Tabs } from "@/design-system/components/Tabs/Tabs";

import PageWithTitle from "@/PageWithTitle";
import FreshpaintLogoSmall from "@/components/FpLogoSmall";
import useAccountStatus from "@/noAccount/useAccountStatus";
import { loginWithAccessCode, loginWithFirebaseToken } from "./api";
import DocsTermsAgreement from "./DocsTermsAgreement";

interface DocsLoginPageProps {
  isSignedIn: boolean;
}

type DocsTab = "login" | "access-code";

// Segmented-toggle styling layered onto the design-system Tabs primitive so we
// keep Radix's tablist a11y/keyboard behavior while matching the docs portal's
// pill look instead of the default underline tabs.
const triggerClassName =
  "flex-1 rounded-md border-b-0 px-3 py-2 text-sm data-[state=active]:bg-surface-default data-[state=active]:text-textDefault data-[state=active]:border-transparent data-[state=active]:shadow-sm";

const GENERIC_ERROR = "Something went wrong. Please try again.";

function DocsLoginPage({ isSignedIn }: DocsLoginPageProps) {
  const routerLocation = useLocation();
  const history = useHistory();

  // GitBook redirects unauthenticated visitors here with ?location=<path>, the
  // page they were trying to reach. An optional ?code=<code> is present on
  // "auto-login" links generated by the admin tool.
  const parsed = queryString.parse(routerLocation.search);
  const docsLocation =
    typeof parsed.location === "string" ? parsed.location : "";
  const initialCode = typeof parsed.code === "string" ? parsed.code : "";

  const currentUser = getCurrentUser();

  // Docs access requires a Freshpaint account, so check up front rather than
  // offering a sign-in that the backend is guaranteed to reject with a 403.
  // get_account_status is dispatched before account resolution, so an accountless
  // user can call it. A temporary access code remains a valid way in, so this only
  // gates the Login tab.
  const { data: accountStatus, isError: accountCheckFailed } = useAccountStatus(
    isSignedIn && !!currentUser,
  );
  const hasNoFreshpaintAccount = accountStatus?.has_account === false;

  // Authenticate without a click when we already have an existing Freshpaint
  // session: we submit immediately and show a loading screen rather than
  // flashing the form. Access-code links are deliberately NOT auto-submitted —
  // temp-access visitors must always be shown, and agree to, the terms of use
  // first — so a prefilled ?code= lands on the access-code tab instead.
  const canAutoLogin = isSignedIn && !!currentUser && !initialCode;

  const [tab, setTab] = useState<DocsTab>(
    initialCode ? "access-code" : "login",
  );
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoLoggingIn, setAutoLoggingIn] = useState(canAutoLogin);

  // Whether the visitor has ticked the terms-of-use agreement for the control
  // currently on screen. Reset on tab change since each tab shows its own.
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Set when the Firebase login attempt reports the user has never accepted
  // the terms; flips the login tab from "Access docs" to the agreement step.
  const [termsRequired, setTermsRequired] = useState(false);

  const goToFreshpaintLogin = () => {
    // Reuse the app's existing Firebase sign-in, then return here (now signed
    // in) via SignInScreen's `next` redirect, preserving the docs location.
    const returnTo = `/docs-login${routerLocation.search}`;
    history.push(`/login?next=${encodeURIComponent(returnTo)}`);
  };

  const handleTabChange = (value: string) => {
    setTab(value as DocsTab);
    setError(null);
    setAgreedToTerms(false);
    setTermsRequired(true);
  };

  const submitFirebase = async (agreeTerms = false) => {
    if (!currentUser) {
      goToFreshpaintLogin();
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const result = await loginWithFirebaseToken(
        idToken,
        docsLocation,
        agreeTerms,
      );
      if (result.status === "terms-required") {
        setTermsRequired(true);
        setLoading(false);
        setAutoLoggingIn(false);
        return;
      }
      window.location.href = result.redirectUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : GENERIC_ERROR);
      setLoading(false);
      setAutoLoggingIn(false);
    }
  };

  const submitAccessCode = async (codeToUse: string) => {
    const trimmed = codeToUse.trim();
    if (!trimmed) {
      setError("Enter your access code.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { redirectUrl } = await loginWithAccessCode(trimmed, docsLocation);
      window.location.href = redirectUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : GENERIC_ERROR);
      setLoading(false);
      setAutoLoggingIn(false);
    }
  };

  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (autoSubmittedRef.current || !canAutoLogin) {
      return;
    }
    // Hold the auto-submit until the account check resolves, otherwise we fire a
    // request that is guaranteed to fail and show the user an error instead of an
    // explanation. If the check itself fails we fall through and attempt the login
    // anyway: the backend is the actual gate, and stranding the user on the loading
    // screen would be worse than showing them its error.
    if (!accountStatus && !accountCheckFailed) {
      return;
    }
    if (accountStatus && !accountStatus.has_account) {
      setAutoLoggingIn(false);
      return;
    }
    autoSubmittedRef.current = true;
    void submitFirebase();
    // submitFirebase is recreated every render and is not a meaningful dependency;
    // autoSubmittedRef keeps this to a single submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoLogin, accountStatus, accountCheckFailed]);

  if (autoLoggingIn && !error) {
    return (
      <PageWithTitle title="Signing in">
        <div className="flex min-h-screen items-center justify-center">
          <p className="text-textSubtle text-sm">Signing you in&hellip;</p>
        </div>
      </PageWithTitle>
    );
  }

  return (
    <PageWithTitle title="Sign in">
      <div className="bg-surface-sunken flex min-h-screen items-center justify-center px-4 py-12">
        <div className="border-borderSubtle bg-surface-default w-[420px] max-w-full rounded-xl border p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="dark:bg-nav-background flex size-10 items-center justify-center rounded-lg bg-gray-700">
              <FreshpaintLogoSmall viewBox="0 0 92 124" className="h-5 w-5" />
            </div>
            <div>
              <div className="text-textDefault font-semibold">
                Freshpaint Docs
              </div>
              <div className="text-textSubtle text-sm">
                Customer documentation portal
              </div>
            </div>
          </div>

          <h2 className="text-textDefault mt-6 text-2xl font-bold">
            Access documentation
          </h2>
          <p className="text-textSubtle mt-1 text-sm">
            If you have a Freshpaint account, access documentation using Login.
            Otherwise, you should have been provided a temporary code from your
            point of contact.
          </p>

          <Tabs.Root
            value={tab}
            onValueChange={handleTabChange}
            size="small"
            className="mt-6"
          >
            <Tabs.List className="bg-surface-muted flex gap-1 rounded-lg p-1">
              <Tabs.Trigger value="login" className={triggerClassName}>
                Login
              </Tabs.Trigger>
              <Tabs.Trigger value="access-code" className={triggerClassName}>
                Temp access
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="login" className="mt-6">
              {isSignedIn && currentUser ? (
                <>
                  <p className="text-textSubtle text-sm">
                    Signed in as{" "}
                    <span className="text-textDefault font-medium">
                      {currentUser.email}
                    </span>
                  </p>
                  {hasNoFreshpaintAccount ? (
                    <>
                      <p className="text-textDefault mt-3 text-sm font-medium">
                        This account doesn&rsquo;t have access to the Freshpaint
                        documentation.
                      </p>
                      <p className="text-textSubtle mt-2 text-sm">
                        Documentation is available to customers and approved
                        users. If you believe you should have access, contact
                        your Freshpaint representative — or use a temporary
                        access code if you have one.
                      </p>
                    </>
                  ) : termsRequired ? (
                    <>
                      <div className="mt-4">
                        <DocsTermsAgreement
                          checked={agreedToTerms}
                          onChange={setAgreedToTerms}
                        />
                      </div>
                      <Button
                        className="mt-4 w-full"
                        size="lg"
                        loading={loading}
                        disabled={!agreedToTerms}
                        onClick={() => submitFirebase(true)}
                      >
                        Agree and access docs
                      </Button>
                    </>
                  ) : (
                    <Button
                      className="mt-4 w-full"
                      size="lg"
                      loading={loading}
                      onClick={() => submitFirebase()}
                    >
                      Access docs
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-textSubtle text-sm">
                    Sign in with your Freshpaint account to continue.
                  </p>
                  <Button
                    className="mt-4 w-full"
                    size="lg"
                    onClick={goToFreshpaintLogin}
                  >
                    Log in with Freshpaint
                  </Button>
                </>
              )}
            </Tabs.Content>

            <Tabs.Content value="access-code" className="mt-6">
              <Input
                labelPosition="stacked"
                id="docs-access-code"
                label="Access code"
                placeholder="E.g. FP-ACME-2024"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && agreedToTerms) {
                    submitAccessCode(code);
                  }
                }}
                autoFocus
              />
              <div className="mt-4">
                <DocsTermsAgreement
                  checked={agreedToTerms}
                  onChange={setAgreedToTerms}
                />
              </div>
              <Button
                className="mt-4 w-full"
                size="lg"
                loading={loading}
                disabled={!agreedToTerms}
                onClick={() => submitAccessCode(code)}
              >
                Access docs
              </Button>
              <p className="text-textSubtle mt-4 text-xs">
                Access codes are reusable until their expiry date. Contact your
                Freshpaint rep to obtain one.
              </p>
            </Tabs.Content>
          </Tabs.Root>

          {error && <p className="text-textError mt-4 text-sm">{error}</p>}
        </div>
      </div>
    </PageWithTitle>
  );
}

export default DocsLoginPage;
